import { Component, OnInit, inject } from "@angular/core";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { WorkspaceButtonConfig } from "@models/workspace/WorkspaceButtonConfig";
import { AdvEffDataService } from "../shared/services/adv-eff-data.service";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { EffectivenessOverviewModel } from "../shared/models/effectiveness-overview-model";
import { FilterType } from "@enums/FilterType";
import { nameof } from "@methods/jeffs-toolkit";
import { ModuleType } from "@enums/ModuleType";
import { ModuleSubType } from "@enums/ModuleSubType";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { firstValueFrom } from "rxjs";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";

@Component({
    selector: "adv-eff-overview",
    templateUrl: "./adv-eff-overview.component.html",
    styleUrls: ["./adv-eff-overview.component.scss"],
    providers: [provideTranslocoScope("controls")],
    imports: [WorkspaceComponent]
})
export class AdvEffOverviewComponent implements OnInit {
    private _advancedTestingService = inject(AdvEffDataService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _permissions = inject(PermissionsService);
    private _transloco = inject(TranslocoService);

    moduleType = ModuleType.Controls;
    appendToModuleType = ModuleSubType[ModuleSubType.AdvEffTesting];
    tabID: string;
    tab: TabModel;
    idProp: string = nameof<EffectivenessOverviewModel>((x) => x.guid);
    data: Promise<EffectivenessOverviewModel[]>;
    headerLookup: { [propName: string]: string } = {};

    private buildHeaderLookup(): { [propName: string]: string } {
        const t = (key: string) => this._transloco.translate(`controls.advEffOverview.${key}`);
        let headerLookup = {};

        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.controlIdentifier)] = t("headerControlId");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.controlName)] = t("headerControlName");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.name)] = t("headerTestPlan");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.testerScoreText)] = t("headerScoreTester");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.reviewerScoreText)] = t("headerScoreReviewer");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.statusDescription)] = t("headerStatus");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.groupName)] = t("headerTestPeriod");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.groupScoreText)] = t("headerScoreTestPeriod");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.startDate)] = t("headerStartDate");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.endDate)] = t("headerEndDate");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.numberOfSamples)] = t("headerNumberOfSamples");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.numberOfSourceDocuments)] = t("headerNumberOfSourceDocuments");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.sourceUploaders)] = t("headerSourceUploaders");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.isSourceUploaderOverdue)] = t("headerIsSourceUploaderOverdue");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.sourceUploaderDueDate)] = t("headerSourceUploaderDueDate");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.evidenceUploaders)] = t("headerEvidenceUploaders");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.evidenceUploaderDueDate)] = t("headerEvidenceUploaderDueDate");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.isEvidenceUploaderOverdue)] = t("headerIsEvidenceUploaderOverdue");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.testers)] = t("headerTesters");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.testerDueDate)] = t("headerTesterDueDate");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.isTesterOverdue)] = t("headerIsTesterOverdue");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.reviewers)] = t("headerReviewers");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.reviewerDueDate)] = t("headerReviewerDueDate");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.isReviewerOverdue)] = t("headerIsReviewerOverdue");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.linkedStandards)] = t("headerLinkedStandards");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.qualityAspects)] = t("headerQualityAspects");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.controlOwner)] = t("headerControlOwner");
        headerLookup[nameof<EffectivenessOverviewModel>((x) => x.organization)] = t("headerOrganization");

        return headerLookup;
    }

    filterConfigs: FilterConfig[] = [
        {
            label: "",
            column: nameof<EffectivenessOverviewModel>((x) => x.organizationId),
            type: FilterType.Organization,
            hideActionColumn: true,
            param: "",
        },
    ];
    rendererConfig: RendererConfig[] = [
        RendererConfig.createScoreConfig(
            nameof<EffectivenessOverviewModel>((x) => x.testerScoreText),
            nameof<EffectivenessOverviewModel>((x) => x.testerScoreColor)
        ),
        RendererConfig.createScoreConfig(
            nameof<EffectivenessOverviewModel>((x) => x.reviewerScoreText),
            nameof<EffectivenessOverviewModel>((x) => x.reviewerScoreColor)
        ),
        RendererConfig.createScoreConfig(
            nameof<EffectivenessOverviewModel>((x) => x.groupScoreText),
            nameof<EffectivenessOverviewModel>((x) => x.groupScoreColor)
        ),
        RendererConfig.createDateConfig(nameof<EffectivenessOverviewModel>((x) => x.startDate)),
        RendererConfig.createDateConfig(nameof<EffectivenessOverviewModel>((x) => x.endDate)),
        RendererConfig.createOverdueConfig(
            nameof<EffectivenessOverviewModel>((x) => x.sourceUploaderDueDate),
            nameof<EffectivenessOverviewModel>((x) => x.isSourceUploaderOverdue)
        ),
        RendererConfig.createOverdueConfig(
            nameof<EffectivenessOverviewModel>((x) => x.evidenceUploaderDueDate),
            nameof<EffectivenessOverviewModel>((x) => x.isEvidenceUploaderOverdue)
        ),
        RendererConfig.createOverdueConfig(
            nameof<EffectivenessOverviewModel>((x) => x.testerDueDate),
            nameof<EffectivenessOverviewModel>((x) => x.isTesterOverdue)
        ),
        RendererConfig.createOverdueConfig(
            nameof<EffectivenessOverviewModel>((x) => x.reviewerDueDate),
            nameof<EffectivenessOverviewModel>((x) => x.isReviewerOverdue)
        ),
    ];

    buttonConfig: WorkspaceButtonConfig[] = [];
    numberProps: string[] = [
        nameof<EffectivenessOverviewModel>((x) => x.numberOfSamples),
        nameof<EffectivenessOverviewModel>((x) => x.numberOfSourceDocuments),
    ];
    dateProps: string[] = [
        nameof<EffectivenessOverviewModel>((x) => x.startDate),
        nameof<EffectivenessOverviewModel>((x) => x.endDate),
        nameof<EffectivenessOverviewModel>((x) => x.sourceUploaderDueDate),
        nameof<EffectivenessOverviewModel>((x) => x.evidenceUploaderDueDate),
        nameof<EffectivenessOverviewModel>((x) => x.testerDueDate),
        nameof<EffectivenessOverviewModel>((x) => x.reviewerDueDate),
    ];

    permissions: WorkspaceModulePermissionModel;

    public async ngOnInit(): Promise<void> {
        this.permissions = this._permissions.permissions.Workspace.advEffTesting;
        await firstValueFrom(this._transloco.load(`controls/${this._transloco.getActiveLang()}`));
        this.headerLookup = this.buildHeaderLookup();
        this.filterConfigs[0].label = this._transloco.translate(
            "controls.advEffOverview.filterOrganizationLabel"
        );
        this.loadData();
    }

    loadData() {
        this.data = firstValueFrom(this._advancedTestingService.getEffectivenessWorkspace());
    }

    open(model: EffectivenessOverviewModel) {
        this._tabService.generateTab(
            this._pages.ControlsTestingAdvEffDetail,
            model.guid,
            this._transloco.translate("controls.advEffOverview.tabTitle", { name: model.name })
        );
    }
}
